import React from 'react';
import { Loader2, Receipt, Paperclip, CheckCircle2, X, Banknote, Settings, Plus, Pencil, Trash2, Check } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface ExpenseCategory {
    id: string;
    label: string;
    job_post_ids?: string[];
}

interface JobPost {
    id: string;
    title: string;
}

interface ExpenseReport {
    id: string;
    employe_id: string;
    first_name?: string;
    last_name?: string;
    amount: number;
    category: string;
    description: string;
    receipt_document_id: string | null;
    status: 'pending' | 'approved' | 'refused' | 'reimbursed';
    refuse_reason: string;
    submitted_at: string;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
    pending: { label: 'En attente', cls: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    approved: { label: 'Approuvée', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    refused: { label: 'Refusée', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    reimbursed: { label: 'Remboursée', cls: 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border-brand-500/20' },
};

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);
const employeeName = (r: ExpenseReport) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Salarié';

export const ExpenseReportsSettings: React.FC = () => {
    const [reports, setReports] = React.useState<ExpenseReport[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [busyId, setBusyId] = React.useState<string | null>(null);
    const [refusingId, setRefusingId] = React.useState<string | null>(null);
    const [refuseReason, setRefuseReason] = React.useState('');
    const [error, setError] = React.useState('');

    const [showSettings, setShowSettings] = React.useState(false);
    const [categories, setCategories] = React.useState<ExpenseCategory[]>([]);
    const [jobPosts, setJobPosts] = React.useState<JobPost[]>([]);
    const [categoriesLoading, setCategoriesLoading] = React.useState(false);
    const [newCategory, setNewCategory] = React.useState('');
    const [editingCategoryId, setEditingCategoryId] = React.useState<string | null>(null);
    const [editingCategoryLabel, setEditingCategoryLabel] = React.useState('');
    const [categoryBusy, setCategoryBusy] = React.useState(false);
    const [categoryError, setCategoryError] = React.useState('');

    const load = () => {
        moduleApi.getExpenseReports().then((d: any) => setReports(Array.isArray(d) ? d : [])).catch(() => setReports([])).finally(() => setLoading(false));
    };
    React.useEffect(() => { load(); }, []);

    const loadCategories = () => {
        setCategoriesLoading(true);
        Promise.all([
            moduleApi.getExpenseCategories().catch(() => []),
            moduleApi.getJobPosts().catch(() => [])
        ]).then(([cats, posts]: any) => {
            setCategories(Array.isArray(cats) ? cats : []);
            setJobPosts(Array.isArray(posts) ? posts : []);
        }).finally(() => setCategoriesLoading(false));
    };

    const toggleCategoryJobPost = async (categoryId: string, jobPostId: string) => {
        const cat = categories.find((c) => c.id === categoryId);
        if (!cat) return;
        const current = cat.job_post_ids || [];
        const next = current.includes(jobPostId) ? current.filter((id) => id !== jobPostId) : [...current, jobPostId];
        setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, job_post_ids: next } : c));
        try {
            const res: any = await moduleApi.setExpenseCategoryJobPosts(categoryId, next);
            if (res?.error) { setCategoryError(res.error); loadCategories(); }
        } catch {
            loadCategories();
        }
    };

    const openSettings = () => {
        setCategoryError('');
        setNewCategory('');
        setEditingCategoryId(null);
        setShowSettings(true);
        loadCategories();
    };

    const handleAddCategory = async () => {
        const label = newCategory.trim();
        if (!label) return;
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.createExpenseCategory(label);
            if (res?.error) { setCategoryError(res.error); return; }
            setNewCategory('');
            loadCategories();
        } finally {
            setCategoryBusy(false);
        }
    };

    const handleRenameCategory = async (id: string) => {
        const label = editingCategoryLabel.trim();
        if (!label) return;
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.updateExpenseCategory(id, label);
            if (res?.error) { setCategoryError(res.error); return; }
            setEditingCategoryId(null);
            loadCategories();
        } finally {
            setCategoryBusy(false);
        }
    };

    const handleDeleteCategory = async (id: string) => {
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.deleteExpenseCategory(id);
            if (res?.error) { setCategoryError(res.error); return; }
            loadCategories();
        } finally {
            setCategoryBusy(false);
        }
    };

    // L'onglet doit s'ouvrir de façon SYNCHRONE au clic (avant tout await) : ouvrir window.open() après un
    // fetch perd le geste utilisateur et se fait bloquer silencieusement par le navigateur (aucune erreur,
    // aucune réaction visible — le bug remonté par Emeni). On ouvre un onglet vide tout de suite, puis on
    // le redirige vers le blob une fois téléchargé.
    const viewReceipt = async (reportId: string, win: Window | null) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/expense-reports/${reportId}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); }
    };

    const handleApprove = async (id: string) => {
        setBusyId(id);
        setError('');
        try {
            const res: any = await moduleApi.setExpenseReportStatus(id, 'approved');
            if (res?.error) { setError(res.error); return; }
            load();
        } finally {
            setBusyId(null);
        }
    };

    const handleRefuse = async (id: string) => {
        if (!refuseReason.trim()) { setError('Merci d\'indiquer un motif.'); return; }
        setBusyId(id);
        setError('');
        try {
            const res: any = await moduleApi.setExpenseReportStatus(id, 'refused', refuseReason.trim());
            if (res?.error) { setError(res.error); return; }
            setRefusingId(null);
            setRefuseReason('');
            load();
        } finally {
            setBusyId(null);
        }
    };

    const handleReimburse = async (id: string) => {
        setBusyId(id);
        setError('');
        try {
            const res: any = await moduleApi.reimburseExpenseReport(id);
            if (res?.error) { setError(res.error); return; }
            load();
        } finally {
            setBusyId(null);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const pending = reports.filter((r) => r.status === 'pending');
    const others = reports.filter((r) => r.status !== 'pending');

    return (
        // Liste de notes de frais : elle gagne à occuper l'écran plutôt qu'une colonne étroite (Emeni, 24/08).
        <div className="space-y-6">
            {/* Pas de largeur maximale : cette page présente une LISTE, pas un formulaire — elle gagne à occuper l'écran d'un ordinateur. Les écrans de réglages gardent leur plafond : un formulaire étalé sur 1900 px devient illisible. Demande d'Emeni (2026-08-25). */}
            <div className="flex items-start justify-between gap-3">
                <p className="text-xs text-content-muted italic">Les notes de frais soumises par vos salariés depuis leur portail.</p>
                <Button variant="secondary" size="sm" onClick={openSettings} className="shrink-0"><Settings size={13} /> Motifs</Button>
            </div>
            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div>
                <h2 className="text-sm font-bold text-content mb-2">En attente {pending.length > 0 && `(${pending.length})`}</h2>
                {pending.length === 0 ? (
                    <p className="text-xs text-content-muted">Aucune note de frais en attente.</p>
                ) : (
                    <div className="space-y-2">
                        {pending.map((r) => (
                            <div key={r.id} className="p-4 rounded-2xl bg-surface-soft border border-border space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-bold text-content text-sm">{formatPrice(r.amount)} <span className="text-content-muted font-normal">— {r.category} — {employeeName(r)}</span></p>
                                        <p className="text-[11px] text-content-muted mt-0.5">Envoyée le {new Date(r.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                </div>
                                {r.description && <p className="text-xs text-content">{r.description}</p>}
                                {r.receipt_document_id && (
                                    <button onClick={() => viewReceipt(r.id, window.open('', '_blank'))}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                        <Paperclip size={11} /> Justificatif
                                    </button>
                                )}
                                {refusingId === r.id ? (
                                    <div className="space-y-2">
                                        <textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={2}
                                            placeholder="Motif du refus" className="w-full bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => { setRefusingId(null); setRefuseReason(''); }} className="px-3 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold transition-all">Annuler</button>
                                            <button onClick={() => handleRefuse(r.id)} disabled={busyId === r.id}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50 transition-all">
                                                <X size={12} /> Confirmer le refus
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => { setRefusingId(r.id); setRefuseReason(''); }} disabled={busyId === r.id}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-bold disabled:opacity-50 transition-all">
                                            <X size={12} /> Refuser
                                        </button>
                                        <button onClick={() => handleApprove(r.id)} disabled={busyId === r.id}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 transition-all">
                                            <CheckCircle2 size={12} /> {busyId === r.id ? 'Envoi...' : 'Approuver'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <h2 className="text-sm font-bold text-content mb-2 flex items-center gap-2"><Receipt size={15} /> Historique</h2>
                {others.length === 0 ? (
                    <p className="text-xs text-content-muted">Aucune note de frais traitée pour l'instant.</p>
                ) : (
                    <div className="space-y-2">
                        {others.map((r) => {
                            const status = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
                            return (
                                <div key={r.id} className="p-4 rounded-2xl bg-surface-soft border border-border space-y-1.5">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold text-content text-sm">{formatPrice(r.amount)} <span className="text-content-muted font-normal">— {r.category} — {employeeName(r)}</span></p>
                                            <p className="text-[11px] text-content-muted mt-0.5">Envoyée le {new Date(r.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                        </div>
                                        <span className={`shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${status.cls}`}>{status.label}</span>
                                    </div>
                                    {r.status === 'refused' && r.refuse_reason && (
                                        <p className="text-xs text-red-400"><span className="font-bold">Motif :</span> {r.refuse_reason}</p>
                                    )}
                                    <div className="flex items-center gap-2">
                                        {r.receipt_document_id && (
                                            <button onClick={() => viewReceipt(r.id, window.open('', '_blank'))}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                                <Paperclip size={11} /> Justificatif
                                            </button>
                                        )}
                                        {r.status === 'approved' && (
                                            <button onClick={() => handleReimburse(r.id)} disabled={busyId === r.id}
                                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-[11px] font-bold disabled:opacity-50 transition-all">
                                                <Banknote size={11} /> Marquer remboursée
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Modal open={showSettings} onClose={() => setShowSettings(false)} title="Motifs de note de frais" icon={<Settings size={16} className="text-brand-500" />} maxWidth="max-w-sm">
                <div className="space-y-4">
                    <p className="text-xs text-content-muted">Ces motifs sont proposés à vos salariés lors de la création d'une note de frais.</p>
                    {categoryError && <p className="text-xs text-red-500 font-semibold">{categoryError}</p>}
                    {categoriesLoading ? (
                        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                    ) : (
                        <div className="space-y-1.5">
                            {categories.map((cat) => (
                                <div key={cat.id} className="px-3 py-2 rounded-lg bg-surface-soft border border-border space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        {editingCategoryId === cat.id ? (
                                            <>
                                                <Input value={editingCategoryLabel} onChange={(e) => setEditingCategoryLabel(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory(cat.id)}
                                                    className="h-7 text-xs flex-1" autoFocus />
                                                <button onClick={() => handleRenameCategory(cat.id)} disabled={categoryBusy} className="p-1 text-emerald-500 hover:bg-emerald-500/10 rounded disabled:opacity-50"><Check size={14} /></button>
                                                <button onClick={() => setEditingCategoryId(null)} className="p-1 text-content-muted hover:text-content rounded"><X size={14} /></button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="flex-1 text-xs text-content font-semibold truncate">{cat.label}</span>
                                                <button onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryLabel(cat.label); }} className="p-1 text-content-muted hover:text-content rounded"><Pencil size={13} /></button>
                                                <button onClick={() => handleDeleteCategory(cat.id)} disabled={categoryBusy} className="p-1 text-content-muted hover:text-red-500 rounded disabled:opacity-50"><Trash2 size={13} /></button>
                                            </>
                                        )}
                                    </div>
                                    {editingCategoryId !== cat.id && jobPosts.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <span className="text-[9px] font-bold text-content-muted uppercase tracking-widest">Visible pour :</span>
                                            {(!cat.job_post_ids || cat.job_post_ids.length === 0) && (
                                                <span className="text-[10px] text-content-muted italic">Tous les postes</span>
                                            )}
                                            {jobPosts.map((jp) => {
                                                const isSelected = (cat.job_post_ids || []).includes(jp.id);
                                                return (
                                                    <button key={jp.id} type="button" onClick={() => toggleCategoryJobPost(cat.id, jp.id)}
                                                        className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${isSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content-muted hover:border-brand-500/40'}`}>
                                                        {jp.title}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                        <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                            placeholder="Nouveau motif" className="h-8 text-xs flex-1" />
                        <Button size="sm" onClick={handleAddCategory} disabled={categoryBusy || !newCategory.trim()}><Plus size={13} /> Ajouter</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
